import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../Components/Themed"
import type { AthleteProps, BestAvailableOrderProps, GolfCourseProps, GolfHoleProps, GolfLeaderProps, GolfTournamentStatisticsProps, LeagueProps, MarketProps, OrderProps, TournamentProps } from '../types';
import { GolfApi, GolfHelpers } from './api';
import { ActivityIndicator, FlatList, Image } from 'react-native';
import { useColors } from '../constants/useColors';
import moment, { type Moment } from 'moment-mini';
import { AthleteImage } from '../Components/Jerseys';
import { Icons, Toggle } from '../Components';
import SearchBox from '../Components/SearchBox';
import LeaderBetSelector from './components/LeaderBetSelector';
import MarketButton from '../MarketComponents/components/MarketButton';
import { MarketComponentHelpers } from '../MarketComponents/api';
import { MarketButtonHelpers } from '../MarketComponents/components/MarketButton/api';
import LeaderBetsList from './components/LeaderBetsList';
import type PostHog from 'posthog-react-native';
import BEPostHog from '../PostHog';

type GolfScoreboardProps = {
    player_id?:string,
    ph_client?:PostHog
    tournament_id:string,
    markets:MarketProps[],
    my_orders:OrderProps[],
    best_available:BestAvailableOrderProps[],
    onOrder:(order:OrderProps) => void,
    onClose:() => void
}

const scorecard_sections = ['course', 'my_action', 'toggle','search','leaderboard', 'open_bets', 'holes'];
const GolfScoreboard = ({ player_id, my_orders, tournament_id, markets, best_available, onOrder, onClose }:GolfScoreboardProps) => {
    const Colors = useColors();
    const [ module_size, setModuleSize ] = useState({ width:0, height:0 });
    const [ active_tab, setActiveTab ] = useState<'scorecard'|'holes'|'bets'>('scorecard');
    const [ search, setSearch ] = useState<string>('');
    const [ my_data, setMyData ] = useState<{
        my_loading:boolean,
        orders:OrderProps[]
    }>({
        my_loading: false,
        orders: []
    });
    const { orders } = my_data;
    let hole_orders = orders.filter(o => markets.filter(mk => mk.length_variable).map(mk => mk.market_id.toString()).includes(o.market_id.toString()));
    let my_hole_orders = my_orders.filter(o => o.event_id == tournament_id && markets.filter(mk => mk.length_variable).map(mk => mk.market_id.toString()).includes(o.market_id.toString()));
    const order_stats = GolfHelpers.calcAllOrderStats(hole_orders);
    const [ hole_details, setHoleDetails ] = useState<number | undefined>(undefined);
    const [ scorecard_state, setScorecard ] = useState<{
        loading:boolean,
        tournament?:TournamentProps,
        league?:LeagueProps,
        athletes:AthleteProps[],
        prior_year?:boolean,
        golf_leaders:GolfLeaderProps[],
        golf_course?:GolfCourseProps,
        golf_statistics?:GolfTournamentStatisticsProps,
        active_round?:number,
        loaded_timestamp?:Moment
    }>({
        loading:false,
        golf_leaders:[],
        athletes:[]
    });
    const { loading, tournament, athletes, golf_leaders, golf_course, league, active_round, golf_statistics, prior_year, loaded_timestamp } = scorecard_state;
    const [ active_leader, setActiveLeader ] = useState<string|undefined>(undefined);
    const [ active_order_leader, setActiveOrderLeader ] = useState<string|undefined>(undefined);
    const order_leader = golf_leaders.find(l => l.athlete_id == active_order_leader);
    const order_athlete = athletes.find(a => a.athlete_id == active_order_leader);
    const golf_leader = golf_leaders.find(l => l.athlete_id == active_leader);
    const golf_athlete = athletes.find(a => a.athlete_id == active_leader);

    let visible_leaders = [ ...golf_leaders ];
    if(search){
        let vis_athletes = athletes.filter(a => `${a.first_name} ${a.last_name}`.toLowerCase().includes(search.toLowerCase())).map(a => a.athlete_id);
        visible_leaders = visible_leaders.filter(l => vis_athletes.includes(l.athlete_id));
    }
    useEffect(() => {
        GolfApi.setEnvironment();
        getData(tournament_id, moment());
        BEPostHog.RecordingActions.recordClip(10);
        BEPostHog.EventActions.captureScreen('Golf', {})
    },[tournament_id]);

    useEffect(() => {
        if(my_hole_orders.length == 0){ return }
        reconcileOrders(my_hole_orders);
    },[JSON.stringify(my_hole_orders)])

    const reconcileOrders = (mho:OrderProps[]) => {
        let new_orders = orders.filter(o => !mho.find(myo => myo.order_id == o.order_id)).concat(mho);
        setMyData({
            ...my_data,
            orders: new_orders
        })
    }

    const getData = async(tournament_id:string, timestamp:Moment):Promise<{ golf_leaders:GolfLeaderProps[], active_round?:number } | undefined> => {
        setScorecard({ ...scorecard_state, loading:true });
        const golf_data = await GolfApi.getScoreboard(tournament_id);
        const tourney = await GolfApi.getTournamentById(tournament_id);
        const golf_stats = await GolfApi.getGolfStats(tournament_id);
        const lg = await GolfApi.getLeagueById(tourney?.league_id ?? '9');
        if(!golf_data){ return undefined }
        if(best_available.length > 0){ setActiveTab('bets') }
        setScorecard({
            ...scorecard_state,
            tournament: tourney,
            league: lg,
            golf_course: golf_data.golf_course,
            golf_leaders: golf_data.golf_leaders,
            golf_statistics: golf_stats?.golf_tournament_statistics,
            prior_year: golf_stats?.prior_year,
            active_round: golf_data.active_round,
            athletes:golf_data.athletes,
            loaded_timestamp: timestamp,
            loading: false
        });

        if(player_id){
            setMyData({ ...my_data, my_loading:true });
            let my_action = await GolfApi.getMyAction(tournament_id, 'tournament');
            //Override orders from the supplied array of orders
            setMyData({ 
                ...my_data,
                my_loading: false,
                orders: orders.filter(o => !my_action.find(no => no.order_id == o.order_id)).concat(my_action)
             })
            
        }

        return { golf_leaders : golf_data.golf_leaders, active_round: golf_data.active_round }
    }

    const getColor = (earnings:number) => {
        if(earnings < 0){ return Colors.text.error }
        if(earnings == 0){ return Colors.text.descriptionLight }
        return Colors.text.success
    }

    const validateOrder = async(order:OrderProps, market:MarketProps, loaded?:Moment):Promise<boolean> => {
        if(!market || !market.length_variable){ return false }
        let difference = 100
        if(loaded){
            difference = moment().diff(loaded,'seconds')
        }
        if(difference > 90){
            //We need to load the data again and confirm that the bet is valid!
            let reloaded_data = await getData(order.event_id, moment());
            if(!reloaded_data){ return false }
            let leader = reloaded_data.golf_leaders.find(gl => gl.athlete_id == order.side_id);
            if(!leader){ return false }
            let curr_hole = GolfHelpers.getCurrentHole(leader, reloaded_data.active_round);
            if(curr_hole == 0){ return true }
            let bet_hole = market.length_variable
            let hole_buffer = bet_hole - curr_hole
            if(hole_buffer < 2){ 
                return false 
            }
        }
        return true
    }

    const renderLeaders = (data:{ item:GolfLeaderProps, index:number }) => {
        const athlete = athletes.find(a => a.athlete_id == data.item.athlete_id);
        if(!athlete){ return <></> }
        let current_round = data.item.leader_rounds.find(r => r.status == 'inprogress');
        let leader_orders = hole_orders.filter(o => o.side_id == data.item.athlete_id);
        let order_stats = GolfHelpers.calcAllOrderStats(leader_orders);
        return (
            <View style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View transparent type='row' style={{ padding:5 }}>
                    {data.item.place != 999 ?
                    <View style={{ padding:5 }}>
                        <Text theme='description'>{GolfHelpers.formatPlace(data.item.place)}</Text>
                    </View>
                    :<></>}
                    <View float style={{ borderRadius:100, padding:2, marginLeft:5 }}>
                        <AthleteImage
                            athlete={athlete}
                            league={league}
                            size={48}
                        />
                    </View>
                    <View transparent style={{ flex:1, marginLeft:5 }}>
                        <Text theme='h2'>{athlete.first_name} {athlete.last_name}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>World Rank: {data.item.rank == 999 ? 'NA' : data.item.rank}</Text>
                    </View>
                    {leader_orders.length > 0 ?
                    <Button float style={{ padding:10, marginRight:10, marginLeft:10, flexDirection:'row', alignItems:'center' }} onPress={() => setActiveOrderLeader(data.item.athlete_id)}>
                        <Icons.OrderIcon color={getColor(order_stats.earnings)} size={18}/>
                        <Text theme='description' style={{ marginLeft:3 }} color={getColor(order_stats.earnings)}>${order_stats.earnings.toFixed(2)}</Text>
                    </Button>
                    :<></>}
                    <View>
                        <Text textAlign='right' theme='h2' color={data.item.score < 0 ? Colors.text.error : Colors.text.h1}>{data.item.score_label}</Text>
                        {data.item.tee_time && current_round?.thru == 0 ?
                        <Text textAlign='right' theme='description' style={{ marginTop:2 }}>{moment(data.item.tee_time).format('HH:mm a')}</Text>
                        :current_round && current_round?.thru > 0 ?
                        <Text textAlign='right' theme='description' style={{ marginTop:2 }}>Thru {current_round.thru}</Text>
                        :<></>}
                    </View>
                    {current_round && current_round.thru < 18 ?
                    <Button
                        title='BET'
                        type='success'
                        style={{ padding:10, marginLeft:10 }}
                        onPress={() => setActiveLeader(data.item.athlete_id)}
                    />
                    :<></>}
                </View>
            </View>
        )
    }

    const renderBets = (data:{ item:BestAvailableOrderProps, index:number }) => {
        if(!tournament){ return <></> }
        const athlete = athletes.find(a => a.athlete_id == data.item.side_id);
        if(!athlete){ return <></> }
        const market = markets.find(m => m.market_id == data.item.market_id)
        if(!market){ return <></> }
        const leader = golf_leaders.find(l => l.athlete_id == data.item.side_id);
        if(!leader){ return <></> }
        const current_hole = GolfHelpers.getCurrentHole(leader, active_round);
        const hole = golf_course?.holes.find(h => h.number == market.length_variable)
        let side_option = market.side_options.find(so => so.side == data.item.side);
        if(!side_option){ return <></> }
        let trade = MarketButtonHelpers.getVisibleTrade('best_available', market, side_option, [], [data.item], data.item.side_id, data.item.side_id); 
        if(!trade){ return <></> }
        return (
            <View style={{ flexDirection:'row', alignItems:'center', padding:5, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View float style={{ borderRadius:100, padding:2, marginLeft:5 }}>
                    <AthleteImage
                        athlete={athlete}
                        league={league}
                        size={48}
                    />
                </View>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{athlete.first_name} {athlete.last_name}</Text>
                    <Text theme='description' style={{ marginTop:5 }}>Current hole: {current_hole}</Text>
                </View>
                <View transparent style={{ alignItems:'center' }}>
                    <Text theme='h2' color={Colors.text.action}>{market.length_variable} - Par {hole?.par}</Text>
                    <Text style={{ marginTop:3 }} theme='light' color={Colors.text.action}>BET HOLE</Text>
                </View>
                <View transparent key={`${tournament.tournament_id}_${athlete.athlete_id}_${data.item.side}`} style={{ margin:2, height:50, width:75, marginLeft:10 }}>
                    <MarketButton
                        side_option={side_option}
                        market={market}
                        trade={trade}
                        show_grades={false}
                        locked={false}
                        order={data.item}
                        onLongPress={() => console.log('LONG PRESSED')}
                        onPress={async(order) => {
                            const order_title = MarketComponentHelpers.getOrderTitleForTournament(order, market, tournament, athlete);
                            if(!order_title){ return alert('Unable to process order') }
                            let new_order = { ...order, title:order_title, league_id:tournament.league_id }
                            const valid = await validateOrder(new_order, market, loaded_timestamp)
                            if(!valid){ return alert('Unable to process order') }
                            onOrder(new_order)
                        }}
                    />
                </View>
            </View>
        )
    }

    const renderGolfers = (data:{ item:GolfLeaderProps, index:number }) => {
        const athlete = athletes.find(a => a.athlete_id == data.item.athlete_id);
        if(!athlete){ return <></> }
        return (
            <View float style={{ margin:4, width:120 }}>
                <View type='header' style={{ borderTopRightRadius:8, borderTopLeftRadius:8, padding:5 }}>
                    <Text theme='h1'>{data.item.score_label}</Text>
                </View>
                <View transparent style={{ padding:10, flex:1 }}>
                    <AthleteImage
                        athlete={athlete}
                        size={48}
                    />
                    <Text style={{ marginTop:5 }} theme='h2' textAlign='center'>{athlete.first_name} {athlete.last_name}</Text>
                </View>
                <View type='footer' style={{ borderBottomLeftRadius:8, borderBottomRightRadius:8, padding:5 }}>
                    <Button
                        title='BET'
                        type='success'
                        style={{ padding:10 }}
                        onPress={() => setActiveLeader(data.item.athlete_id)}
                    />
                </View>
            </View>
        )
    }

    const renderHoles = (data:{ item: GolfHoleProps, index:number }) => {
        let golfers = GolfHelpers.getGolfersOnHole(golf_leaders, data.item.number, active_round);
        let hole_stat = GolfHelpers.getHoleStats(data.item.number, golf_statistics, active_round);
        const expanded = data.item.number == hole_details ? true : false
        return (
            <View>
                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View transparent style={{ flex:1 }}>
                        <Text theme='h1'>Hole: {data.item.number}{data.item.name ? ` - ${data.item.name}`: ''} - Par {data.item.par}</Text>
                        {hole_stat ?
                        <Text theme='description' style={{ marginTop:3 }}>Stroke Avg {'\u2022'} {hole_stat.strokes_avg}{hole_stat?.previous_round ? hole_stat.previous_round: ''}</Text>
                        :<></>}
                    </View>
                    <Button float style={{ padding:5, flexDirection:'row', alignItems:'center', borderRadius:4 }} onPress={() => expanded ? setHoleDetails(undefined) : setHoleDetails(data.item.number)}>
                        <Icons.GolferIcon size={18} color={Colors.text.action}/>
                        <Text theme='action' size={18} style={{ marginLeft:5 }}>{golfers.length}</Text>
                    </Button>
                </View>
                {expanded ?
                <View style={{ padding:10 }}>
                    <FlatList
                        key={'hole_golfer_list'}
                        data={golfers}
                        horizontal
                        renderItem={renderGolfers}
                        keyExtractor={(item) => `${data.item.number}_${item.athlete_id.toString()}`}
                    />
                </View>
                :<></>}
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'course':
                if(!golf_course){ return <></> }
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10}}>
                        {league ?
                        <Image
                            source={{ uri: league.league_image }}
                            style={{ height:50, width:50 }}
                            resizeMode='cover'
                        />
                        :<></>}
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>{golf_course.course_name}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Par {golf_course.par} - {golf_course.yardage} yards</Text>
                        </View>
                    </View>
                )
            case 'my_action':
                if(module_size.width == 0){ return <></> }
                if(hole_orders.length == 0){ return <></> }
                let padding = (3 * 10) + 20
                
                const kpi_card_width = ((module_size.width - padding) / 3)
                return (
                    <View>
                        <View style={{ padding:10 }}>
                            <Text theme='h1'>My Hole By Hole Action</Text>
                        </View>
                        <View style={{ flexDirection:'row', padding:10 }}>
                            <View float style={{ margin:5, padding:10, width:kpi_card_width }}>
                                <Text theme='h1'>${(order_stats.resolved_stake+order_stats.unresolved_stake).toFixed(2)}</Text>
                                <Text theme='description' style={{ marginTop:5 }}>Total Bet</Text>
                            </View>
                            <View float style={{ margin:5, padding:10, width:kpi_card_width }}>
                                <Text theme='h1'>${order_stats.unresolved_stake.toFixed(2)}</Text>
                                <Text theme='description' style={{ marginTop:5 }}>Unresolved Bets</Text>
                            </View>
                            <View float style={{ margin:5, padding:10, width:kpi_card_width }}>
                                <Text theme='h1' color={getColor(order_stats.earnings)}>${order_stats.earnings.toFixed(2)}</Text>
                                <Text theme='description' style={{ marginTop:5 }} color={getColor(order_stats.earnings)}>Earnings</Text>
                            </View>
                        </View>

                    </View>
                )
            case 'toggle':
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            options={[
                                { key: 'scorecard', label: 'Leaderboard' },
                                { key: 'holes', label: 'Holes' }
                            ]}
                            selected_option={active_tab}
                            onSelectOption={(key) => setActiveTab(key as 'scorecard'|'holes')}
                        />
                    </View>
                )
            case 'search':
                return (
                    <View style={{ padding:10 }}>
                        <SearchBox
                            hide_search_button
                            onChange={(text) => setSearch(text)}
                        />
                    </View>
                )
            case 'open_bets':
                if(active_tab != 'bets'){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ borderTopWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                            <Text theme='h2'>Hole Betting</Text>
                            <Text theme='description' style={{ marginTop:4 }}>Bet on over / under par on each hole!  Par results in a no-bet.</Text>
                        </View>
                        <FlatList
                            data={best_available.sort((a,b) => moment(b.expire_datetime).unix() - moment(a.expire_datetime).unix())}
                            renderItem={renderBets}
                            key={'tourney_bets'}
                            keyExtractor={(item) => item.order_id.toString()}
                        />
                    </View>
                )
            case 'leaderboard':
                if(active_tab != 'scorecard'){ return <></> }
                return (
                    <FlatList
                        data={visible_leaders.sort((a,b) => a.place - b.place || a.rank - b.rank)}
                        keyExtractor={(item) => item.athlete_id.toString()}
                        windowSize={10}
                        maxToRenderPerBatch={15}
                        renderItem={renderLeaders}
                        key='leader_list'
                    />
                )
            case 'holes':
                if(!golf_course){ return <></> }
                if(active_tab != 'holes'){ return <></> }
                return (
                    <FlatList
                        data={golf_course.holes.sort((a,b) => b.number - a.number)}
                        key={'hole_list'}
                        renderItem={renderHoles}
                        keyExtractor={(item) => item.number.toString()}
                    />
                )
            default: return <></>
        }
    }
    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }} onLayout={(ev) => {
                const { width, height } = ev.nativeEvent.layout
                setModuleSize({ width, height });
            }}>
                {loading ?
                <ActivityIndicator
                    style={{ padding:10, alignSelf:'center' }}
                    size={'large'}
                    color={Colors.text.h1}
                />
                :<></>}
                <FlatList
                    data={scorecard_sections}
                    key={'golf_list'}
                    refreshing={loading}
                    onRefresh={() => getData(tournament_id, moment())}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            <View type='footer' style={{ padding:10 }}>
                <Button
                    title='CLOSE'
                    type='close'
                    style={{ padding:10 }}
                    onPress={() => onClose()}
                />
            </View>
            {tournament && order_leader && order_athlete ?
            <LeaderBetsList
                markets={markets}
                max_height={module_size.height - 50}
                orders={hole_orders}
                active_round={active_round}
                golf_leader={order_leader}
                athlete={order_athlete}
                onClose={() => setActiveOrderLeader(undefined)}

            />
            :<></>}
            {tournament && golf_leader && golf_course && active_round && golf_athlete ?
            <LeaderBetSelector
                golf_leader={golf_leader}
                golf_course={golf_course}
                my_orders={hole_orders}
                markets={markets}
                best_available_orders={best_available.filter(o => o.side_id == golf_athlete.athlete_id)}
                latest_trades={[]}
                tournament={tournament}
                active_round={active_round}
                prior_year={prior_year}
                athlete={golf_athlete}
                golf_statistics={golf_statistics}
                onClose={() => setActiveLeader(undefined)}
                onOrder={async(order) => {
                    let market = markets.find(m => m.market_id == order.market_id);
                    if(!market){ return alert('Unable to process') }
                    const valid = await validateOrder(order, market, loaded_timestamp);
                    if(!valid){ return alert('Unable to process this order') }
                    onOrder(order)
                }}
            />
            :<></>}
        </View>
    )
}

export default GolfScoreboard